import React, { useCallback, useEffect, useRef } from 'react';
import { Provider , useDispatch} from 'react-redux';
import useJourneyInactivity, {
  useSessionTimeoutScopeActive,
} from './hook/inactivityTimerHook';
import { Modal } from './common/components';
import { MemoryRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "./store";
import {
  TermInsuranceJourneyLead,
  TermInsuranceJourneyQuote,
  TermInsuranceJourneySummary,
  TermInsuranceJourneyCustomerVerification,
  TermInsuranceJourneyProposal,
  PaymentReturn,
  ProposalReview,
  TrackForm,
} from './term-insurance-journey';
import ErrorBoundary from './common/components/ErrorBoundary';
import {
  clearJourneySessionState,
  getRestartPath,
  getRestartPageUrl,
  JOURNEY_START_PATH_KEY,
  JOURNEY_INITIATED_PAGE_URL_KEY,
  setStoredApplicationNumber,
  setStoredCaseId,
} from './common/utils';
import {
  decryptJourneyQueryValue,
  encryptJourneyQueryValue,
  resolveJourneyApplicationNumbers,
} from './common/utils/journeyEncryption';
import { REINITIATE_PAYMENT_SUMMARY_SESSION_KEY } from './constants';
import { journeyActions } from './store/journeySlice';
import { getJourneyData } from './api/journeyService';
import {
  hydrateStoreFromJourney,
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
} from './common/utils/journeyResume';
import { logError } from './common/utils/logger';

const STAGE_TO_PATH = {
  lead: '/',
  quote: '/quote',
  summary: '/summary',
  customerVerification: '/verification',
  proposal: '/proposal',
};
const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';
const getResumeDobStorageKey = (applicationNumber) =>
  `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber || "").trim()}`;

function JourneyRoutes() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const hydratedAppRef = useRef(null);
  const isHydratingRef = useRef(false);

  // Remember where the user started the journey: in-app path and full page URL (for inactivity restart).
  // Only set when on / or /home so we don't overwrite after redirect to /term-plan?applicationNumber=...
  // For /term-plan, set in-app path only if not already set (user opened term-plan page directly).
  useEffect(() => {
    try {
      if (location.pathname === '/' || location.pathname === '/home') {
        sessionStorage.setItem(JOURNEY_START_PATH_KEY, location.pathname);
        const pageUrl = typeof window !== 'undefined' && window.location
          ? `${window.location.origin}${window.location.pathname}`
          : '';
        if (pageUrl) sessionStorage.setItem(JOURNEY_INITIATED_PAGE_URL_KEY, pageUrl);
      } else if (location.pathname === '/term-plan' && !sessionStorage.getItem(JOURNEY_START_PATH_KEY)) {
        sessionStorage.setItem(JOURNEY_START_PATH_KEY, '/term-plan');
      }
    } catch {
      /* ignore */
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    if (location.pathname === '/summary') return;
    try {
      sessionStorage.removeItem(REINITIATE_PAYMENT_SUMMARY_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, [location.pathname]);

  // Global journey resume:
  // Any route opened with ?applicationNumber=... should rehydrate store from backend
  // and land on the exact stage where user left off.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const applicationNumberCipher = params.get('applicationNumber')?.trim() || null;
    const applicationNumberPlain = applicationNumberCipher
      ? resolveJourneyApplicationNumbers(applicationNumberCipher).plain
      : null;
    const dateOfBirthCipher = params.get('dateOfBirth')?.trim() || null;
    const dateOfBirthFromUrl = dateOfBirthCipher
      ? (decryptJourneyQueryValue(dateOfBirthCipher) || dateOfBirthCipher)
      : null;
    let dateOfBirthFromSession = null;
    try {
      dateOfBirthFromSession =
        (applicationNumberPlain
          ? sessionStorage.getItem(getResumeDobStorageKey(applicationNumberPlain))
          : null) ||
        sessionStorage.getItem(RESUME_DOB_SESSION_KEY) ||
        null;
    } catch {
      dateOfBirthFromSession = null;
    }
    const resumeDob = dateOfBirthFromUrl || dateOfBirthFromSession;
    const fromPaymentReturn = params.get('fromPaymentReturn');
    let reinitiateSummarySession = false;
    try {
      reinitiateSummarySession =
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem(REINITIATE_PAYMENT_SUMMARY_SESSION_KEY) === '1';
    } catch {
      reinitiateSummarySession = false;
    }
    if (!applicationNumberPlain) return;
    if (location.pathname === '/payment-return') return;
    if (hydratedAppRef.current === applicationNumberPlain || isHydratingRef.current) return;

    let cancelled = false;
    isHydratingRef.current = true;
    if (!resumeDob) {
      window.location.href = '/resume-journey';
      return;
    }

    // Always call backend with decrypted application number.
    getJourneyData(applicationNumberPlain, resumeDob || undefined)
      .then((journey) => {
        if (cancelled || !journey || typeof journey !== 'object') return;

        hydrateStoreFromJourney(
          dispatch,
          journey,
          applicationNumberPlain,
          store.getState,
        );

        const journeyAppNumber =
          resolveJourneyApplicationNumbers(
            journey.applicationNumber ||
              journey.application_number ||
              applicationNumberPlain,
          ).plain || applicationNumberPlain;
        const journeyCaseId =
          journey.caseId ||
          journey.c_caseId ||
          journey.case_number ||
          null;
        if (journeyAppNumber) setStoredApplicationNumber(journeyAppNumber);
        if (journeyCaseId) setStoredCaseId(journeyCaseId);

        // Ensure URL param is encrypted for the rest of the journey (no impact if key missing).
        try {
          const encrypted = encryptJourneyQueryValue(journeyAppNumber || applicationNumberPlain);
          if (encrypted && applicationNumberCipher && encrypted !== applicationNumberCipher) {
            params.set('applicationNumber', encrypted);
            const nextSearch = params.toString();
            navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
          }
        } catch {
          // ignore
        }

        // Payment return: `targetStage` in the URL is not authoritative — same rules as refresh:
        // Stage 5.0 gateway uses `transactionStatus` on the stage payload (SUCCESS → verification, else summary).
        const resumeRoute = getResumeRouteFromJourney(journey);
        const resumeState =
          resumeRoute === '/proposal'
            ? getResumeStateFromJourney(journey)
            : null;
        // Failed payment: PaymentReturn sends user to /summary to reinitiate pay. Journey
        // currentStage may still be payment/verification, so resumeRoute would be /verification —
        // do not override. `fromPaymentReturn` is dropped from URL after resume; session flag keeps this stable.
        const stayOnSummaryAfterFailedPayment =
          location.pathname === '/summary' &&
          (fromPaymentReturn === '1' || reinitiateSummarySession);
        if (
          resumeRoute &&
          location.pathname !== resumeRoute &&
          !stayOnSummaryAfterFailedPayment
        ) {
          navigate(resumeRoute, resumeState ? { state: resumeState } : {});
        }

        hydratedAppRef.current = applicationNumberPlain;
        // Keep app-scoped DOB in session for downstream protected calls (e.g. payment return callback)
        // when backend session ownership context is unavailable.
      })
      .catch((err) => {
        logError('[JourneyRoutes] Global resume hydration failed', err);
      })
      .finally(() => {
        isHydratingRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, location.pathname, location.search]);

  const handleRestart = useCallback(() => {
    const restartPageUrl = getRestartPageUrl();
    const restartPath = getRestartPath();
    clearJourneySessionState({ clearLocalStorage: true, purgeReduxPersist: true });
    dispatch(journeyActions.resetJourney());
    if (restartPageUrl) {
      window.location.href = restartPageUrl;
    } else {
      navigate(restartPath);
    }
  }, [dispatch, navigate]);

  /** Idle session modal only on term-plan / payment-success / payment-failure pages, not site-wide. */
  const sessionTimeoutScopeActive = useSessionTimeoutScopeActive();
  const { showModal, secondsLeft, closeModal, restartJourney } = useJourneyInactivity(
    handleRestart,
    sessionTimeoutScopeActive,
  );

  return (
    <>
      <Routes>
        <Route path="/" element={<TermInsuranceJourneyLead mode="basic" />} />
        <Route path="/home" element={<TermInsuranceJourneyLead mode="basic" />} />
        <Route path="/term-plan" element={<TermInsuranceJourneyLead mode="personalize" />} />
        <Route path="/payment-return" element={<PaymentReturn />} />
        <Route path="/quote" element={<TermInsuranceJourneyQuote />} />
        <Route path="/summary" element={<TermInsuranceJourneySummary />} />
        <Route path="/verification" element={<TermInsuranceJourneyCustomerVerification />} />
        <Route path="/proposal" element={<TermInsuranceJourneyProposal />} />
        <Route path="/proposal-review" element={<ProposalReview />} />
        <Route path="/track-your-application" element={<TrackForm />} />
      </Routes>

      <Modal
        isOpen={sessionTimeoutScopeActive && showModal}
        onClose={secondsLeft === 0 ? restartJourney : closeModal}
        title="Session Timeout"
        modalNode="session"
      >
        <p>
          You have been inactive. Your session will expire in{" "}
          <strong>{secondsLeft}</strong> seconds.
        </p>

        <div className="modal-footer">
          <button
            className="btn secondary-button regular"
            disabled={secondsLeft > 0}
            onClick={restartJourney}
          >
            Continue
          </button>
          <button
            className="btn primary-button regular"
            onClick={secondsLeft === 0 ? restartJourney : closeModal}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}

function App({ initialStage = "lead", initialPath: initialPathProp, resetJourneyOnMount = false }) {
  const leadCompleted = sessionStorage.getItem("leadCompleted");

  const initialPath =
    initialPathProp != null
      ? initialPathProp
      : leadCompleted === "true"
        ? "/quote"
        : STAGE_TO_PATH[initialStage] || "/";

  // After refresh we clear session in main.jsx but the store was already created with old localStorage.
  useEffect(() => {
    if (resetJourneyOnMount) {
      store.dispatch(journeyActions.resetJourney());
    }
  }, [resetJourneyOnMount]);

  return (
    <Provider store={store}>
       <PersistGate loading={null} persistor={persistor}>
        <MemoryRouter initialEntries={[initialPath]}>
          <div className="online-sales-journey-container">
            <ErrorBoundary>
              <JourneyRoutes />
            </ErrorBoundary>
          </div>
        </MemoryRouter>
       </PersistGate>
    </Provider>
  );
}

export default App;
